import { useCallback, useEffect, useState } from 'react';
import { IconBrain, IconCheck, IconDatabase, IconLoader2 } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Analyse IA du dossier en DEUX temps (Maxim 2026-06-02) :
 *
 * 1. « Digérer » — indexation RAG (Voyage Law-2) + extraction structurelle
 *    (parties, dates/timeline, métadonnées). Bon marché, à lancer
 *    routinièrement : rend le dossier interrogeable dans claude.ai et
 *    navigable dans l'app, sans appel Sonnet.
 * 2. « Synthétiser » — l'appel Claude coûteux : 4 recherches (charges /
 *    décharges / synthèse / droit) + détecteurs (contradictions, alertes
 *    procédurales). À lancer à la demande, une fois le dossier digéré.
 *
 * Origine : « gros bouton après OCR = bam le RAG se lance d'abord et tous
 * les outils vont pomper dessus » → scindé pour éviter de payer la
 * synthèse à chaque fois.
 */

type Status = {
  indexed_pages: number;
  total_pages_ocr: number;
  coverage: number;
  has_voyage: boolean;
  can_analyze: boolean;
};

type Props = {
  dossierId: string;
};

export default function AnalyseIaButton({ dossierId }: Props) {
  const [status, setStatus] = useState<Status | null>(null);
  const [digesting, setDigesting] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.get<Status>(`/dossiers/${dossierId}/analyze-ia/status`);
      setStatus(s);
      return s;
    } catch (e) {
      // Silencieux — l'endpoint peut ne pas être déployé en local.
      return null;
    }
  }, [dossierId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // Auto-refresh (Maxim 2026-06-29) : tant que le dossier n'est pas pleinement
  // indexé, on re-poll le statut — le bouton se dégrise et la coverage monte
  // sans rechargement manuel après un import/OCR de fond. S'arrête à 100%.
  const needsPolling =
    digesting || !status || !(status.total_pages_ocr > 0 && status.coverage >= 1);
  useEffect(() => {
    if (!needsPolling) return;
    const id = window.setInterval(() => {
      void loadStatus().then((s) => {
        if (s && s.coverage >= 1 && digesting) {
          setDigesting(false);
          toast.success('Dossier digéré — RAG prêt, parties et dates en cours');
        }
      });
    }, 4000);
    return () => window.clearInterval(id);
  }, [needsPolling, digesting, loadStatus]);

  const onDigest = async (force: boolean = false) => {
    if (!status || !status.can_analyze) {
      toast.error(
        status && !status.has_voyage
          ? 'Voyage AI non configuré côté serveur'
          : "Aucune page OCR disponible — OCRise d'abord les PDFs"
      );
      return;
    }
    setDigesting(true);
    try {
      // 1. Indexation Voyage des pages OCR (async Celery) → RAG / claude.ai.
      await api.post(`/dossiers/${dossierId}/reindex`);
      // 2. Extraction structurelle (parties, dates/timeline, métadonnées).
      const result = await api.post<{ status?: string; message?: string }>(
        `/dossiers/${dossierId}/ai-research/digest`,
        { force }
      );
      toast.success(
        result.status === 'already_running'
          ? 'Une analyse IA tourne déjà sur ce dossier.'
          : 'Digestion lancée (RAG + parties + dates). Recharge dans ~30s.'
      );
      void loadStatus();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la digestion');
      setDigesting(false);
    }
  };

  const onSynthesize = async (force: boolean = false) => {
    if (!status || !(status.total_pages_ocr > 0 && status.coverage >= 1)) {
      toast.error('Digère d’abord le dossier (indexation RAG requise pour la synthèse).');
      return;
    }
    setSynthesizing(true);
    try {
      const result = await api.post<{ status?: string; message?: string }>(
        `/dossiers/${dossierId}/ai-research/synthesize`,
        { force }
      );
      toast.success(
        result.status === 'already_running'
          ? 'Une analyse IA tourne déjà sur ce dossier.'
          : 'Synthèse IA lancée en arrière-plan — recharge l’onglet Synthèse IA dans ~90s.'
      );
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec du lancement de la synthèse');
    } finally {
      setSynthesizing(false);
    }
  };

  if (!status) return null;

  const pct = Math.round((status.coverage || 0) * 100);
  const fullyIndexed = status.total_pages_ocr > 0 && status.coverage >= 1;
  const busy = digesting || synthesizing;

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-rule bg-paper-card px-3 py-2">
      <div className="flex items-center gap-2">
        <IconBrain size={18} stroke={1.6} className="text-accent" />
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-medium text-text-primary">Analyse IA du dossier</p>
          <p className="text-[11px] text-text-tertiary">
            {status.total_pages_ocr === 0
              ? "Aucune page OCR (uploade et OCRise d'abord les PDFs)"
              : fullyIndexed
                ? `${status.indexed_pages} page(s) indexée(s) — RAG prêt`
                : `${status.indexed_pages}/${status.total_pages_ocr} pages indexées (${pct}%)`}
          </p>
        </div>
        {fullyIndexed && (
          <span className="flex items-center gap-1 text-[11px] text-cat-fav">
            <IconCheck size={14} stroke={2} /> digéré
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Digérer — RAG + parties/dates. Bon marché, routinier. */}
        <div className="flex items-center gap-1">
          <Btn
            type="button"
            variant={digesting ? 'ghost' : fullyIndexed ? 'secondary' : 'primary'}
            size="sm"
            onClick={() => void onDigest(false)}
            disabled={busy || !status.can_analyze}
            title="Indexe les pages OCR (RAG, utilisable dans claude.ai) + extrait parties, dates et métadonnées. Bon marché, à lancer à chaque dossier. Incrémental."
          >
            {digesting ? (
              <>
                <IconLoader2 size={14} stroke={1.6} className="animate-spin" />
                Digestion…
              </>
            ) : (
              <>
                <IconDatabase size={14} stroke={1.6} />
                {fullyIndexed ? 'Re-digérer' : 'Digérer'}
              </>
            )}
          </Btn>
          {fullyIndexed && !busy && (
            <button
              type="button"
              onClick={() => void onDigest(true)}
              className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              title="Forcer une re-digestion complète (ignorer le cache)"
            >
              ↻
            </button>
          )}
        </div>

        {/* Synthétiser — appel Claude coûteux, à la demande. */}
        <div className="flex items-center gap-1">
          <Btn
            type="button"
            variant={synthesizing ? 'ghost' : 'primary'}
            size="sm"
            onClick={() => void onSynthesize(false)}
            disabled={busy || !fullyIndexed}
            title={
              fullyIndexed
                ? '4 recherches Sonnet (charges, décharges, synthèse, droit) + détecteurs (contradictions, alertes procédurales). Appel Claude coûteux — à lancer quand tu en as besoin.'
                : 'Digère d’abord le dossier — la synthèse a besoin du RAG.'
            }
          >
            {synthesizing ? (
              <>
                <IconLoader2 size={14} stroke={1.6} className="animate-spin" />
                Synthèse…
              </>
            ) : (
              <>
                <IconBrain size={14} stroke={1.6} />
                Synthétiser
              </>
            )}
          </Btn>
          {fullyIndexed && !busy && (
            <button
              type="button"
              onClick={() => void onSynthesize(true)}
              className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              title="Forcer une re-synthèse complète (ignorer le cache)"
            >
              ↻
            </button>
          )}
        </div>
      </div>

      {!fullyIndexed && status.total_pages_ocr > 0 && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-paper-subtle">
          <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
